<template>
  <!--
    WP-03 -- text + send. WP-03-CHAT-VISUAL-PARITY (doc 11, D2): the
    prototype's microphone button is rendered exactly as it draws it but is
    INERT -- `aria-disabled`, out of the tab order, no handler (voice is out of
    scope).

    STUDENT CHAT V2 -- IMAGE ATTACHMENT FLOW: the camera button opens V2's
    image-source menu («گرفتن عکس» / «انتخاب از گالری»); the chosen photo
    shows as V2's `.composer-media-preview` above the composer with a remove
    control, and V2's own rule applies -- a photo goes out WITH a short note
    saying which question/part to check (send stays disabled until there is
    text, with V2's `.composer-status-note--image` hint). One photo per
    message, as V2 draws it. Only jpg/png/webp up to 10MB are accepted -- the
    upstream contract -- decided from the file's bytes, not its name.

    The menu and its backdrop sit OUTSIDE the form: the form's own
    `backdrop-filter` would otherwise become the containing block of their
    `position: fixed`.

    V405-B (doc 26 §6): `docked` is presentation only. Before the first turn
    the chat start stage holds this same composer in normal flow, drawn as the
    rendered V405 reference draws it; every control, handler and emitted
    payload is the one below. With `docked` false nothing here changes.

    WB-M1A (doc 23 §13.4.3): `panel` is a second, separate presentation, for
    the chat panel of the board Workspace -- the same composer in normal flow
    at the foot of a panel, in its ordinary active-chat look. It is NOT the
    `docked` start-state card and does not reuse it. Opt-in: with `panel`
    false (every existing caller) nothing here changes.

    POST-UAT-UX / PUX-B (doc 35/01 §2.1): `boardEntry` adds the rendered V405
    Board trigger (`.borna-whiteboard-trigger`, inserted by `ensureTrigger()`
    right before Send): one more tool button, the V405 glyph, «باز کردن
    وایت‌برد». It only emits `open-board`; the host opens the Board. Its own
    `boardEntryDisabled` (busy / guided), never the composer's `disabled`:
    the Board costs nothing, so a zero balance does not close it. Opt-in:
    without `boardEntry` (the Board Workspace's panel) nothing changes.

    Package 38 UX-4 (doc 38/00 §5, owner-approved deviation DEV-38-1 from
    V405's order): with `boardEntry` the Board trigger and the microphone
    swap places -- camera · Board · input · microphone · send. The DOM is
    reordered (never CSS `order`), so keyboard order follows; actions,
    labels, disabled states and the voice states are unchanged. Without
    `boardEntry` (the Workspace panel, every other caller) the markup is
    byte-for-byte what it was.
  -->
  <div
    class="ai-tutor-composer-dock"
    :class="{
      'ai-tutor-composer-dock--docked': docked,
      'ai-tutor-composer-dock--panel': panel,
    }"
  >
    <input
      ref="galleryInput"
      type="file"
      class="ai-tutor-composer__file"
      data-testid="ai-tutor-image-gallery-input"
      :accept="accept"
      tabindex="-1"
      aria-hidden="true"
      @change="onFileChange($event, 'gallery')"
    />
    <input
      ref="cameraInput"
      type="file"
      class="ai-tutor-composer__file"
      data-testid="ai-tutor-image-camera-input"
      :accept="accept"
      capture="environment"
      tabindex="-1"
      aria-hidden="true"
      @change="onFileChange($event, 'camera')"
    />

    <template v-if="menuOpen">
      <button
        type="button"
        class="ai-tutor-image-menu__backdrop"
        :aria-label="$t('aiTutorSetup.chat.image.closeMenu')"
        @click="menuOpen = false"
      ></button>
      <div class="ai-tutor-image-menu__anchor" :style="menuAnchorStyle">
        <section
          class="ai-tutor-image-menu"
          :style="menuStyle"
          role="dialog"
          aria-modal="true"
          :aria-label="$t('aiTutorSetup.chat.image.menuLabel')"
          data-testid="ai-tutor-image-menu"
        >
          <header>
            <div>
              <strong>{{ $t('aiTutorSetup.chat.image.menuTitle') }}</strong>
              <span>{{ $t('aiTutorSetup.chat.image.menuSubtitle') }}</span>
            </div>
            <button
              type="button"
              :aria-label="$t('aiTutorSetup.chat.image.close')"
              @click="menuOpen = false"
            >
              <student-home-icon name="close" :size="18" />
            </button>
          </header>
          <div>
            <!-- P40 TR-022: a desktop browser ignores `capture`, so this row
                 only opened the same file picker as the gallery row. It is
                 offered where a camera capture really happens. -->
            <button
              v-if="!desktopPointer"
              type="button"
              class="ai-tutor-image-menu__action"
              data-testid="ai-tutor-image-source-camera"
              @click="pick('camera')"
            >
              <span
                class="ai-tutor-image-menu__icon ai-tutor-image-menu__icon--camera"
              >
                <student-home-icon name="camera" :size="22" />
              </span>
              <span>
                <strong>{{ $t('aiTutorSetup.chat.image.camera') }}</strong>
                <small>{{ $t('aiTutorSetup.chat.image.cameraHint') }}</small>
              </span>
              <student-home-icon name="arrow" :size="17" />
            </button>
            <button
              type="button"
              class="ai-tutor-image-menu__action"
              data-testid="ai-tutor-image-source-gallery"
              @click="pick('gallery')"
            >
              <span class="ai-tutor-image-menu__icon">
                <student-home-icon name="book" :size="22" />
              </span>
              <span>
                <strong>{{ $t('aiTutorSetup.chat.image.gallery') }}</strong>
                <small>{{ $t('aiTutorSetup.chat.image.galleryHint') }}</small>
              </span>
              <student-home-icon name="arrow" :size="17" />
            </button>
          </div>
          <p
            v-if="desktopPointer"
            class="ai-tutor-image-menu__paste-hint"
            data-testid="ai-tutor-image-paste-hint"
          >
            {{ $t('aiTutorSetup.chat.image.pasteHint') }}
          </p>
        </section>
      </div>
    </template>

    <form
      ref="form"
      class="ai-tutor-composer"
      :class="{
        'ai-tutor-composer--first-question': firstQuestion && !guided,
        'ai-tutor-composer--guided': guided && !image,
        'ai-tutor-composer--typing': typing,
        'ai-tutor-composer--keyboard': typing && keyboardInset > 0,
      }"
      :style="
        typing && keyboardInset > 0
          ? { '--ai-tutor-keyboard-inset': `${keyboardInset}px` }
          : null
      "
      @focusin="onFormFocusIn"
      @focusout="onFormFocusOut"
      :data-helper="
        guided && !image
          ? $t('aiTutorSetup.chat.feedback.composerHelper')
          : null
      "
      data-testid="ai-tutor-composer"
      @submit.prevent="onSubmit"
    >
      <!-- CR-03 (doc 28 §17): the optional `notice` slot (the zero-credit
           warning) shares this area, so it is anchored above the fixed
           composer in the active chat and takes its own row in the docked
           start composer -- exactly like the photo preview and notes. -->
      <div
        v-if="image || note || voiceAbove || $scopedSlots.notice"
        class="ai-tutor-composer__above"
      >
        <slot name="notice" />
        <div
          v-if="image"
          class="ai-tutor-composer__preview"
          data-testid="ai-tutor-image-preview"
        >
          <img
            :src="image.previewUrl"
            :alt="$t('aiTutorSetup.chat.image.previewAlt')"
          />
          <span>
            <strong>{{
              image.source === 'camera'
                ? $t('aiTutorSetup.chat.image.readyCamera')
                : $t('aiTutorSetup.chat.image.readyGallery')
            }}</strong>
            <small>{{ $t('aiTutorSetup.chat.image.readyHint') }}</small>
          </span>
          <button
            type="button"
            data-testid="ai-tutor-image-remove"
            :aria-label="$t('aiTutorSetup.chat.image.remove')"
            @click="removeImage"
          >
            <student-home-icon name="close" :size="17" />
          </button>
        </div>
        <div
          v-if="note"
          class="ai-tutor-composer__note"
          :class="`ai-tutor-composer__note--${note.kind}`"
          :role="note.kind === 'error' ? 'alert' : 'status'"
          data-testid="ai-tutor-composer-note"
        >
          <student-home-icon
            :name="note.icon || (note.kind === 'error' ? 'camera' : 'spark')"
            :size="15"
          />
          {{ note.text }}
        </div>
        <!-- 37_VOICE_TO_TEXT: V405's voice card (`composer-media-preview
             --voice`) and its notes -- the ready reminder and the error. -->
        <template v-for="voiceItem in voiceAboveItems">
          <div
            v-if="voiceItem === 'ready'"
            :key="`${voiceItem}-card`"
            class="ai-tutor-composer__preview ai-tutor-composer__preview--voice"
            data-testid="ai-tutor-voice-ready"
          >
            <span class="ai-tutor-composer__voice-ready-icon">
              <student-home-icon name="mic" :size="18" />
            </span>
            <span>
              <strong>{{ $t('aiTutorSetup.chat.voice.readyTitle') }}</strong>
              <small>{{
                $t('aiTutorSetup.chat.voice.readyHint', {
                  seconds: localDigits(voice.durationSec),
                })
              }}</small>
            </span>
            <button
              type="button"
              data-testid="ai-tutor-voice-dismiss"
              :aria-label="$t('aiTutorSetup.chat.voice.readyRemove')"
              @click="onVoiceDismiss"
            >
              <student-home-icon name="close" :size="17" />
            </button>
          </div>
          <div
            v-if="voiceItem === 'ready'"
            :key="`${voiceItem}-note`"
            class="ai-tutor-composer__note"
            role="status"
            data-testid="ai-tutor-voice-ready-note"
          >
            <student-home-icon name="check" :size="15" />
            {{ $t('aiTutorSetup.chat.voice.readyNote') }}
          </div>
          <div
            v-if="voiceItem === 'error'"
            :key="`${voiceItem}-note`"
            class="ai-tutor-composer__note ai-tutor-composer__note--error"
            role="alert"
            data-testid="ai-tutor-voice-error"
          >
            <student-home-icon name="mic" :size="15" />
            {{ $t(`aiTutorSetup.chat.voice.errors.${voice.noteKey}`) }}
          </div>
        </template>
      </div>

      <!-- 37_VOICE_TO_TEXT: while V405 listens or transcribes, its bar takes
           the composer's row; the draft input stays mounted (hidden), so the
           Student's text is never lost. (`v-for` over at most one phase:
           with no voice nothing at all is rendered here, not even an empty
           v-if placeholder.) V405 keeps its Board trigger, disabled, at the
           start of the row meanwhile. -->
      <button
        v-for="entry in voiceBusyBoardEntry"
        :key="entry"
        type="button"
        class="ai-tutor-composer__tool ai-tutor-composer__board"
        data-testid="ai-tutor-composer-board"
        disabled
        :aria-label="$t('aiTutorBoard.composerEntry')"
        :title="$t('aiTutorBoard.composerEntry')"
      >
        <board-icon name="boardEntry" :size="19" />
      </button>
      <template v-for="voiceBar in voiceBars">
        <div
          v-if="voiceBar === 'recording'"
          :key="voiceBar"
          class="ai-tutor-composer__voice-bar"
          role="status"
          aria-live="polite"
          data-testid="ai-tutor-voice-recording"
        >
          <button
            type="button"
            class="ai-tutor-composer__voice-cancel"
            data-testid="ai-tutor-voice-cancel"
            :aria-label="$t('aiTutorSetup.chat.voice.cancel')"
            @click="$emit('voice-cancel')"
          >
            <student-home-icon name="close" :size="19" />
          </button>
          <div class="ai-tutor-composer__voice-copy">
            <span class="ai-tutor-composer__voice-dot"></span>
            <span>
              <strong>{{ $t('aiTutorSetup.chat.voice.listening') }}</strong>
              <!-- The live region announces «listening»; not every second. -->
              <small data-testid="ai-tutor-voice-timer" aria-hidden="true">{{
                voiceTimer
              }}</small>
            </span>
          </div>
          <span class="ai-tutor-composer__voice-wave" aria-hidden="true">
            <i v-for="bar in 24" :key="bar"></i>
          </span>
          <button
            ref="voiceStop"
            type="button"
            class="ai-tutor-composer__voice-stop"
            data-testid="ai-tutor-voice-stop"
            :aria-label="$t('aiTutorSetup.chat.voice.stop')"
            @click="$emit('voice-stop')"
          >
            <student-home-icon name="check" :size="20" />
          </button>
        </div>
        <div
          v-else
          :key="voiceBar"
          class="ai-tutor-composer__voice-transcribing"
          role="status"
          aria-live="polite"
          data-testid="ai-tutor-voice-transcribing"
        >
          <span class="ai-tutor-composer__voice-spinner"></span>
          <span>
            <strong>{{ $t('aiTutorSetup.chat.voice.transcribing') }}</strong>
            <small>{{ $t('aiTutorSetup.chat.voice.transcribingHint') }}</small>
          </span>
        </div>
      </template>

      <button
        v-if="!voiceBusy"
        type="button"
        class="ai-tutor-composer__tool"
        :class="{ 'ai-tutor-composer__tool--active': menuOpen }"
        data-testid="ai-tutor-composer-camera"
        :disabled="disabled || guided"
        :aria-label="$t('aiTutorSetup.chat.addImage')"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        @click="menuOpen = !menuOpen"
      >
        <student-home-icon name="camera" :size="22" />
      </button>
      <!-- Package 38 UX-4 (DEV-38-1): with the Board trigger the row is
           camera · Board · input · microphone · send -- the DOM order, so the
           tab order is the visual order. (`v-for` over at most one entry:
           without a Board trigger nothing at all is rendered here.) -->
      <button
        v-for="entry in boardBeforeInput"
        :key="entry"
        ref="boardEntry"
        type="button"
        class="ai-tutor-composer__tool ai-tutor-composer__board"
        data-testid="ai-tutor-composer-board"
        :disabled="boardEntryDisabled"
        :aria-label="$t('aiTutorBoard.composerEntry')"
        :title="$t('aiTutorBoard.composerEntry')"
        @click="onOpenBoard"
      >
        <board-icon name="boardEntry" :size="19" />
      </button>
      <button
        v-if="voice && !voiceBusy && !boardEntry"
        ref="voiceMic"
        type="button"
        class="ai-tutor-composer__tool"
        data-testid="ai-tutor-composer-mic"
        :disabled="disabled || guided"
        :aria-label="$t('aiTutorSetup.chat.recordVoice')"
        @click="onVoiceStart"
      >
        <student-home-icon name="mic" :size="22" />
      </button>
      <button
        v-else-if="!voice && !boardEntry"
        type="button"
        class="ai-tutor-composer__tool"
        data-testid="ai-tutor-composer-mic"
        aria-disabled="true"
        tabindex="-1"
        :aria-label="$t('aiTutorSetup.chat.recordVoice')"
        @click.prevent
      >
        <student-home-icon name="mic" :size="22" />
      </button>
      <!-- P39 TR-010 (D-B1-1): the V405 key contract -- Enter sends, Shift+Enter
           never sends, an Enter that ends an IME composition never sends --
           with Shift+Enter now inserting a line break. A one-row textarea at
           the input's exact fixed height: longer text scrolls inside it
           (D-B1-1v), so the composer row and everything anchored to it keep
           their measured geometry. On touch keyboards Enter sends (D-B1-1m). -->
      <textarea
        ref="input"
        v-model="draft"
        rows="1"
        enterkeyhint="send"
        class="ai-tutor-composer__input"
        data-testid="ai-tutor-composer-input"
        :hidden="voiceBusy"
        :disabled="disabled"
        :placeholder="placeholder"
        :aria-label="placeholder"
        @keydown.enter="onEnter"
        @paste="onPaste"
      ></textarea>
      <!-- Package 38 UX-4: the microphone's place when the Board trigger
           leads the row -- the same two buttons, in the slot the trigger had
           (so without a Board trigger the markup is exactly what it was). -->
      <button
        v-if="boardEntry && voice && !voiceBusy"
        ref="voiceMic"
        type="button"
        class="ai-tutor-composer__tool"
        data-testid="ai-tutor-composer-mic"
        :disabled="disabled || guided"
        :aria-label="$t('aiTutorSetup.chat.recordVoice')"
        @click="onVoiceStart"
      >
        <student-home-icon name="mic" :size="22" />
      </button>
      <button
        v-else-if="boardEntry && !voice"
        type="button"
        class="ai-tutor-composer__tool"
        data-testid="ai-tutor-composer-mic"
        aria-disabled="true"
        tabindex="-1"
        :aria-label="$t('aiTutorSetup.chat.recordVoice')"
        @click.prevent
      >
        <student-home-icon name="mic" :size="22" />
      </button>
      <button
        v-if="!voiceBusy"
        type="submit"
        class="ai-tutor-composer__send"
        data-testid="ai-tutor-composer-send"
        :disabled="disabled || !canSend"
        :aria-label="$t('aiTutorSetup.chat.sendButton')"
      >
        <student-home-icon name="send" :size="18" />
      </button>
    </form>
  </div>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import BoardIcon from '@/components/AiTutor/Board/BoardIcon.vue'
import { convertHeicToJpeg } from '@/services/aiTutor/heicImage'
import {
  TUTOR_IMAGE_ACCEPT,
  TUTOR_IMAGE_ACCEPT_DESKTOP,
  validateTutorImage,
  createPreviewUrl,
  revokePreviewUrl,
} from '@/services/aiTutor/imageFile'

// P40 QA-22: Subway's and the Tutor's turn-text limit (characters, after
// trim), and where the counter starts (90%).
const TEXT_MAX = 4000
const TEXT_COUNTER_FROM = 3600

// P40 QA-47: `borna-mobile` (the composer's fixed-above-the-nav range); a
// smaller visual-viewport gap is browser chrome, not a keyboard; and how long
// a blur waits before the composer leaves its typing place.
const PHONE_QUERY = '(max-width: 767.98px)'
const KEYBOARD_MIN_INSET = 80
const BLUR_GRACE_MS = 250
// P40 QA-44: `borna-phone`, where the menu is V2's two-tile sheet, and its
// smallest distance to the screen edge.
const PHONE_MENU_QUERY = '(max-width: 620.98px)'
// P40 TR-022: no camera capture behind a file input here.
const DESKTOP_POINTER_QUERY = '(hover: hover) and (pointer: fine)'
const MENU_EDGE = 8
// The bottom navigation is outside this component: it reads one class on
// <html> (StudentBottomNav.vue).
const TYPING_CLASS = 'borna-composer-typing'
function setTypingFlag(on) {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle(TYPING_CLASS, on)
}

export default {
  name: 'Composer',
  components: { StudentHomeIcon, BoardIcon },
  props: {
    // WP-03 binding lifecycle: the composer stays disabled until the SSE
    // subscription is open (opened before the composer becomes usable) and
    // while a send is already in flight.
    disabled: {
      type: Boolean,
      default: false,
    },
    // The prototype's `chat-composer--first-question` state: an indigo
    // border/shadow and the "write your question" placeholder until the
    // conversation has started.
    firstQuestion: {
      type: Boolean,
      default: false,
    },
    // STUDENT CHAT V2 -- V2's `chat-composer--guided`: after a «نفهمیدم»
    // with no re-teach, a helper bubble above the composer and a guided
    // placeholder ask what is unclear. The message is an ordinary turn. V2
    // disables the photo action in this state.
    guided: {
      type: Boolean,
      default: false,
    },
    // V405-B -- the chat start state: in flow inside the start stage instead
    // of fixed to the viewport floor. Presentation only.
    docked: {
      type: Boolean,
      default: false,
    },
    // WB-M1A -- the board Workspace's chat panel: in flow at the foot of the
    // panel instead of fixed to the viewport floor. Presentation only.
    panel: {
      type: Boolean,
      default: false,
    },
    // POST-UAT-UX / PUX-B -- the V405 Board trigger before Send (the host
    // decides whether the Board exists for this Student and conversation).
    boardEntry: {
      type: Boolean,
      default: false,
    },
    boardEntryDisabled: {
      type: Boolean,
      default: false,
    },
    // 37_VOICE_TO_TEXT (doc 37/01 §3.4) -- the host's voice-to-text state
    // (services/aiTutor/voiceInput.js): {phase, elapsed, durationSec,
    // noteKey}. Null (the default, and whenever Subway does not offer voice)
    // keeps the inert microphone exactly as before. Presentation only: the
    // composer emits `voice-start` / `voice-stop` / `voice-cancel` /
    // `voice-dismiss`; the host records, uploads and transcribes, and hands
    // the text back through `appendDraft`.
    voice: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      draft: '',
      // Set on submit: while a send is in flight the input is disabled (as in
      // the reference console), which drops focus -- it is handed back once
      // the composer is usable again, but never grabbed on first mount.
      refocus: false,
      // P40 TR-009: a HEIC photo being turned into a JPEG.
      preparingImage: false,
      menuOpen: false,
      // {file, fileFormat, previewUrl, source} -- owned (and its object URL
      // revoked) here until it is handed to the store on submit.
      image: null,
      // 'type' | 'too_large' | 'empty' -- the last rejected pick.
      imageError: '',
      // P40 QA-47: something in the composer has the focus (debounced on
      // the way out, so a tap on Send never moves the composer mid-tap) and
      // how far the on-screen keyboard reaches up the layout viewport.
      focusWithin: false,
      keyboardInset: 0,
      phoneWidth: false,
      // P40 QA-44: on a phone, where the image menu opens -- measured from
      // the composer it belongs to ({bottom, maxHeight} in px), or null.
      menuPlace: null,
      // P40 TR-022: a mouse-and-hover desktop, where `capture` means nothing.
      desktopPointer: false,
    }
  },
  computed: {
    // P40 QA-47: the fixed active-chat composer on a phone while the Student
    // is typing in it: the bottom navigation steps aside and the composer
    // sits on the keyboard (or on the screen's bottom edge). The docked and
    // panel composers are in normal flow and never change.
    typing() {
      return this.focusWithin && this.phoneWidth && !this.docked && !this.panel
    },
    menuAnchorStyle() {
      return this.menuPlace ? { bottom: `${this.menuPlace.bottom}px` } : null
    },
    menuStyle() {
      return this.menuPlace
        ? { maxHeight: `${this.menuPlace.maxHeight}px` }
        : null
    },
    // V2: text is always required; a photo travels WITH it.
    // P40 QA-22: never over the 4,000-character limit Subway and the Tutor
    // apply (counted the same way: after trim), whether the text was typed,
    // pasted or put into the draft by the host (`setDraft` / `appendDraft`).
    canSend() {
      const length = this.textLength
      return length > 0 && length <= TEXT_MAX && !this.preparingImage
    },
    // P40 TR-009: desktop pickers hide .heic unless it is listed; phones keep
    // the contract's three types (iOS Photos then hands over a JPEG itself).
    accept() {
      return this.desktopPointer
        ? TUTOR_IMAGE_ACCEPT_DESKTOP
        : TUTOR_IMAGE_ACCEPT
    },
    textLength() {
      return this.draft.trim().length
    },
    placeholder() {
      if (this.image) return this.$t('aiTutorSetup.chat.image.placeholder')
      if (this.guided) {
        return this.$t('aiTutorSetup.chat.feedback.composerPlaceholder')
      }
      return this.firstQuestion
        ? this.$t('aiTutorSetup.chat.composerPlaceholder')
        : this.$t('aiTutorSetup.chat.composerPlaceholderFollowUp')
    },
    voicePhase() {
      return this.voice ? this.voice.phase : 'idle'
    },
    // V405: while listening or transcribing its bar replaces the composer's
    // tools, so nothing can be sent or opened meanwhile.
    voiceBusy() {
      return (
        this.voicePhase === 'recording' || this.voicePhase === 'transcribing'
      )
    },
    voiceAbove() {
      return (
        this.voicePhase === 'ready' ||
        (this.voicePhase === 'error' && !!this.voice.noteKey)
      )
    },
    voiceAboveItems() {
      return this.voiceAbove ? [this.voicePhase] : []
    },
    voiceBars() {
      return this.voiceBusy ? [this.voicePhase] : []
    },
    voiceBusyBoardEntry() {
      return this.boardEntry && this.voiceBusy ? ['board'] : []
    },
    // Package 38 UX-4: the idle Board trigger, ahead of the input.
    boardBeforeInput() {
      return this.boardEntry && !this.voiceBusy ? ['board'] : []
    },
    // D-12: m:ss, in the interface's digits.
    voiceTimer() {
      const seconds = Math.max(0, (this.voice && this.voice.elapsed) || 0)
      const text = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(
        2,
        '0'
      )}`
      return this.localDigits(text)
    },
    note() {
      if (this.imageError) {
        return {
          kind: 'error',
          text:
            this.imageError === 'too_large'
              ? this.$t('aiTutorSetup.chat.image.tooLarge')
              : this.imageError === 'heic_failed'
              ? this.$t('aiTutorSetup.chat.image.heicFailed')
              : this.$t('aiTutorSetup.chat.image.unreadable'),
        }
      }
      // P40 TR-009: the visible conversion state; Send waits for it.
      if (this.preparingImage) {
        return {
          kind: 'image',
          icon: 'image',
          text: this.$t('aiTutorSetup.chat.image.preparing'),
        }
      }
      // P40 QA-22: over the limit the draft is kept (never cut) and Send is
      // refused with a reason; near it, a counter.
      if (this.textLength > TEXT_MAX) {
        return {
          kind: 'error',
          icon: 'close',
          text: this.$t('aiTutorSetup.chat.textOverLimit', {
            max: this.localDigits(TEXT_MAX),
          }),
        }
      }
      if (this.image && !this.draft.trim()) {
        return {
          kind: 'image',
          text: this.$t('aiTutorSetup.chat.image.needText'),
        }
      }
      if (this.textLength >= TEXT_COUNTER_FROM) {
        return {
          kind: 'limit',
          icon: 'spark',
          text: this.$t('aiTutorSetup.chat.textCounter', {
            count: this.localDigits(this.textLength),
            max: this.localDigits(TEXT_MAX),
          }),
        }
      }
      return null
    },
  },
  watch: {
    typing: {
      immediate: true,
      handler(now) {
        setTypingFlag(now)
        this.$nextTick(this.placeMenu)
      },
    },
    // P40 QA-44: V2 wants the phone menu "clearly above the chat composer
    // and bottom navigation" -- above the composer as it really is (the
    // docked start composer, the typing position), not a fixed 148 px. The
    // keyboard is closed first so the menu has the screen.
    menuOpen(now) {
      if (!now) {
        this.menuPlace = null
        return
      }
      if (this.phoneMenuQuery() && this.$refs.input) this.$refs.input.blur()
      this.$nextTick(this.placeMenu)
    },
    keyboardInset() {
      this.$nextTick(this.placeMenu)
    },
    // 37_VOICE_TO_TEXT: the focused control disappears when the bar comes and
    // goes -- the keyboard lands on Stop while listening, back on the
    // microphone after a cancel or a failure (`appendDraft` focuses the draft
    // on success).
    voicePhase(now, before) {
      const busy = phase => phase === 'recording' || phase === 'transcribing'
      let target = null
      if (now === 'recording') target = 'voiceStop'
      else if (busy(before) && (now === 'idle' || now === 'error')) {
        target = 'voiceMic'
      }
      if (!target) return
      this.$nextTick(() => {
        // a ref inside the bar's v-for is an array
        const ref = this.$refs[target]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    disabled(now, before) {
      if (now) this.menuOpen = false
      if (before && !now && this.refocus) {
        this.refocus = false
        this.$nextTick(() => {
          const input = this.$refs.input
          if (input && typeof input.focus === 'function') input.focus()
        })
      }
    },
  },
  mounted() {
    // P40 QA-47
    this.viewport = typeof window !== 'undefined' ? window.visualViewport : null
    if (this.viewport) {
      this.viewport.addEventListener('resize', this.measureKeyboard)
      this.viewport.addEventListener('scroll', this.measureKeyboard)
    }
    this.phoneQuery =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(PHONE_QUERY)
        : null
    if (this.phoneQuery) {
      this.onPhoneQuery(this.phoneQuery)
      this.phoneQuery.addListener(this.onPhoneQuery)
    }
    // P40 TR-022
    this.desktopQuery =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(DESKTOP_POINTER_QUERY)
        : null
    if (this.desktopQuery) {
      this.onDesktopQuery(this.desktopQuery)
      this.desktopQuery.addListener(this.onDesktopQuery)
    }
  },
  beforeDestroy() {
    if (this.image) revokePreviewUrl(this.image.previewUrl)
    // P40 QA-47
    clearTimeout(this.blurTimer)
    if (this.viewport) {
      this.viewport.removeEventListener('resize', this.measureKeyboard)
      this.viewport.removeEventListener('scroll', this.measureKeyboard)
    }
    if (this.phoneQuery) this.phoneQuery.removeListener(this.onPhoneQuery)
    if (this.desktopQuery) {
      this.desktopQuery.removeListener(this.onDesktopQuery)
    }
    setTypingFlag(false)
  },
  methods: {
    // P40 QA-47 -- the on-screen keyboard: what of the layout viewport's
    // bottom the visual viewport no longer shows (0 without a keyboard, and
    // where the browser resizes the layout viewport itself).
    measureKeyboard() {
      const viewport = this.viewport
      if (!viewport) return
      const hidden = window.innerHeight - (viewport.height + viewport.offsetTop)
      this.keyboardInset = hidden > KEYBOARD_MIN_INSET ? Math.round(hidden) : 0
    },
    onPhoneQuery(query) {
      this.phoneWidth = !!(query && query.matches)
    },
    onDesktopQuery(query) {
      this.desktopPointer = !!(query && query.matches)
    },
    phoneMenuQuery() {
      return (
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia(PHONE_MENU_QUERY).matches
      )
    },
    // P40 QA-44: 10 px above the composer's top, capped to the viewport.
    placeMenu() {
      const form = this.$refs.form
      if (!this.menuOpen || !form || !this.phoneMenuQuery()) {
        this.menuPlace = null
        return
      }
      const top = form.getBoundingClientRect().top
      const bottom = Math.max(
        MENU_EDGE,
        Math.round(window.innerHeight - top + 10)
      )
      this.menuPlace = {
        bottom,
        maxHeight: Math.max(160, window.innerHeight - bottom - MENU_EDGE),
      }
    },
    // P43-07: the Board trigger is a button, not a text field. Focus on it --
    // a tap, or the focus handed back when the Board closes (PUX-B) -- opens
    // no keyboard, so it must not read as typing: that hid the bottom
    // navigation with nothing on screen to explain it.
    isBoardEntry(el) {
      const ref = this.$refs.boardEntry
      const button = Array.isArray(ref) ? ref[0] : ref
      return !!(el && button && el === button)
    },
    onFormFocusIn(event) {
      clearTimeout(this.blurTimer)
      this.focusWithin = !this.isBoardEntry(event && event.target)
      this.measureKeyboard()
    },
    onFormFocusOut() {
      clearTimeout(this.blurTimer)
      this.blurTimer = setTimeout(() => {
        const active = document.activeElement
        this.focusWithin = !!(
          active &&
          this.$el.contains(active) &&
          !this.isBoardEntry(active)
        )
      }, BLUR_GRACE_MS)
    },
    // PUX-B: the host hands the focus back to the trigger when the Board
    // closes.
    focusBoardEntry() {
      this.$nextTick(() => {
        // a ref inside a v-for is an array (Package 38 UX-4)
        const ref = this.$refs.boardEntry
        const button = Array.isArray(ref) ? ref[0] : ref
        if (button && typeof button.focus === 'function') button.focus()
      })
    },
    // Focus once the current render (which may re-enable the input) is done.
    focus() {
      this.$nextTick(() => {
        const input = this.$refs.input
        if (input && typeof input.focus === 'function') input.focus()
      })
    },
    // STUDENT-UX-02 / UX2-01 (D-2): the host puts a text into the draft
    // (the Learn question that could not be auto-sent at zero credit). The
    // draft stays the composer's own state -- nothing else holds it.
    setDraft(text) {
      this.draft = typeof text === 'string' ? text : ''
      this.focus()
    },
    // 37_VOICE_TO_TEXT (D-3): the transcript goes AFTER whatever the Student
    // had already typed, one space between; an empty text changes nothing.
    appendDraft(text) {
      const addition = typeof text === 'string' ? text.trim() : ''
      if (!addition) return
      this.draft = this.draft.trim()
        ? `${this.draft.replace(/\s+$/, '')} ${addition}`
        : addition
      this.focus()
    },
    localDigits(value) {
      const text = String(value)
      const locale = this.$i18n && this.$i18n.locale
      if (locale !== 'fa') return text
      return text.replace(/[0-9]/g, digit =>
        String.fromCharCode(0x06f0 + Number(digit))
      )
    },
    // V405: the microphone closes the photo menu before it listens.
    onVoiceStart() {
      if (this.disabled || this.guided || this.voiceBusy) return
      this.menuOpen = false
      this.$emit('voice-start')
    },
    onVoiceDismiss() {
      this.$emit('voice-dismiss')
      this.focus()
    },
    // P39 TR-017: a new image source starts clean. The Board share goes
    // straight to the store (never through this composer's submit), so the
    // previous file's error is cleared when the Board opens, and when another
    // file is about to be picked; a new rejection sets its own error again.
    onOpenBoard() {
      this.imageError = ''
      this.$emit('open-board')
    },
    // The Board Workspace panel calls this after its own share was sent.
    clearImageError() {
      this.imageError = ''
    },
    pick(source) {
      this.menuOpen = false
      this.imageError = ''
      const input =
        source === 'camera' ? this.$refs.cameraInput : this.$refs.galleryInput
      if (input && typeof input.click === 'function') input.click()
    },
    async onFileChange(event, source) {
      const input = event && event.target
      const file = input && input.files && input.files[0]
      // Picking the same file again must fire `change` again.
      if (input) input.value = ''
      if (!file) return
      await this.attachFile(file, source)
    },
    // P40 TR-022: an image pasted into the message (Ctrl/Cmd+V) is attached
    // exactly like a picked one -- the same byte check, limits and preview.
    // Only an image-only paste is taken over: a clipboard that carries text
    // (Word, Excel, OneNote also put a picture of the text next to it) is
    // left to the browser and nothing is attached.
    async onPaste(event) {
      const data = event && event.clipboardData
      if (!data || this.disabled || this.voiceBusy) return
      if (Array.from(data.types || []).includes('text/plain')) return
      const items = Array.from(data.items || [])
      const item = items.find(
        entry => entry.kind === 'file' && /^image\//.test(entry.type || '')
      )
      const file = item && item.getAsFile()
      if (!file) return
      event.preventDefault()
      this.imageError = ''
      await this.attachFile(file, 'paste')
    },
    async attachFile(picked, source) {
      const attempt = (this.attachSeq = (this.attachSeq || 0) + 1)
      // A new pick replaces any conversion still running for an older one.
      this.preparingImage = false
      let file = picked
      let result = await validateTutorImage(file)
      // P40 TR-009: a HEIC/HEIF photo is converted to a JPEG first, then
      // checked like any other picture (the 10 MB cap is the JPEG's).
      if (!result.ok && result.reason === 'heic') {
        this.preparingImage = true
        try {
          file = await convertHeicToJpeg(picked)
        } catch (err) {
          if (attempt !== this.attachSeq || this._isDestroyed) return
          this.preparingImage = false
          this.imageError =
            err && err.reason === 'too_large' ? 'too_large' : 'heic_failed'
          return
        }
        if (attempt !== this.attachSeq || this._isDestroyed) return
        this.preparingImage = false
        result = await validateTutorImage(file)
      }
      // A newer pick (or removal) replaced this one meanwhile.
      if (attempt !== this.attachSeq || this._isDestroyed) return
      if (!result.ok) {
        this.imageError =
          result.reason === 'heic' ? 'heic_failed' : result.reason
        return
      }
      this.imageError = ''
      if (this.image) revokePreviewUrl(this.image.previewUrl)
      this.image = {
        file,
        fileFormat: result.fileFormat,
        previewUrl: createPreviewUrl(file),
        source,
      }
      this.focus()
    },
    removeImage() {
      this.attachSeq = (this.attachSeq || 0) + 1
      this.preparingImage = false
      if (this.image) revokePreviewUrl(this.image.previewUrl)
      this.image = null
      this.imageError = ''
      this.focus()
    },
    // P39 TR-010: a textarea has no implicit form submission, so this is the
    // only Enter path to `onSubmit` (whose guards still decide).
    onEnter(event) {
      if (event.isComposing || event.keyCode === 229) return
      if (event.shiftKey) return
      event.preventDefault()
      this.onSubmit()
    },
    onSubmit() {
      if (this.disabled || !this.canSend || this.voiceBusy) return
      const text = this.draft.trim()
      this.draft = ''
      this.refocus = true
      // The voice card and its notes belong to the draft that just left.
      if (this.voicePhase === 'ready' || this.voicePhase === 'error') {
        this.$emit('voice-dismiss')
      }
      if (this.image) {
        // The preview's object URL now belongs to the optimistic message.
        const image = this.image
        this.image = null
        this.imageError = ''
        this.$emit('submit', { text, image })
        return
      }
      this.imageError = ''
      this.$emit('submit', { text })
    },
  },
}
</script>

<style lang="scss" scoped>
// Measured from the rendered canonical prototype (`.chat-composer`, sha256
// 75678bee...): 920x60, r18, 8px padding, 7px gap, shadow 0 16px 45px, fixed
// 20px above the viewport bottom.
//
// WP-04V-D (D10): `position: fixed`, as the prototype has it. It previously
// approximated that with `position: sticky` inside the chat column, which put
// the composer in the right place at rest but let it travel with the column
// under scroll. Fixed positioning means the horizontal box is no longer
// inherited from the column, so it is reconstructed from the same tokens the
// column itself uses -- gutter, column width and (desktop only) the sidebar --
// rather than from viewport math. The chat column reserves the composer's
// height as bottom padding now that it is out of flow.
$ai-tutor-text: $borna-text;
$ai-tutor-muted: $borna-muted;
$ai-tutor-border: $borna-border;
$ai-tutor-canvas: $borna-canvas;
$ai-tutor-indigo: $borna-indigo-600;
$ai-tutor-indigo-300: $borna-indigo-300;

.ai-tutor-composer {
  position: fixed;
  bottom: 20px;
  // Mobile insets by default; the desktop rule below adds the sidebar and the
  // column gutter. `max-width` + auto inline margins then centre the composer
  // on exactly the same axis as the chat column.
  left: 11px;
  right: 11px;
  max-width: $borna-chat-column;
  margin-inline: auto;
  z-index: 18;
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 60px;
  padding: 8px;
  border: 1px solid $ai-tutor-border;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(16px);
  box-shadow: 0 16px 45px rgba(7, 20, 71, 0.14);

  &:focus-within,
  &--first-question {
    border-color: $ai-tutor-indigo-300;
  }

  &--first-question {
    box-shadow: 0 16px 45px rgba(49, 75, 214, 0.18);
  }

  // STUDENT CHAT V2 -- V2 `.chat-composer--guided` and its `:before` helper.
  &--guided {
    border-color: $ai-tutor-indigo;
    box-shadow: 0 16px 45px rgba(49, 75, 214, 0.2),
      0 0 0 4px rgba(49, 75, 214, 0.07);

    &::before {
      content: attr(data-helper);
      position: absolute;
      right: 8px;
      bottom: calc(100% + 8px);
      left: 8px;
      padding: 8px 11px;
      border: 1px solid $borna-indigo-100;
      border-radius: 11px;
      background: rgba(248, 250, 255, 0.98);
      box-shadow: 0 7px 18px rgba(7, 20, 71, 0.08);
      color: $borna-indigo-900;
      font-size: 9px;
      font-weight: 800;
      line-height: 1.6;
      text-align: start;
    }
  }
}

.ai-tutor-composer__tool {
  display: grid;
  place-items: center;
  flex: none;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 0;
  border-radius: 11px;
  background: $ai-tutor-canvas;
  color: $ai-tutor-muted;
  cursor: default;
}

// P39 TR-010: a one-row textarea that renders like the input it replaced. The
// line-height (the value the input inherited, 11px x 1.75) and the block
// padding put one line in the same fixed height where the input drew it. The
// input snaps its centred line to whole pixels depending on where the field
// lands on the page; these top paddings are the ones that land on the same
// pixel as the input at every sub-pixel position (all 64 layout fractions,
// placeholder and text, at 1440 and 390), which is why the split is not
// (42 - 19.25) / 2 each. More lines scroll inside.
.ai-tutor-composer__input {
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 11.5px 0 11.25px;
  border: 0;
  outline: 0;
  background: transparent;
  color: $ai-tutor-text;
  font-family: inherit;
  line-height: 1.75;
  resize: none;
  overflow-x: hidden;
  overflow-y: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  // Empty, the field shows its placeholder on one line, as the input did (a
  // narrow docked phone field would otherwise wrap it onto a hidden second
  // line and offer a scrollbar).
  &:placeholder-shown {
    white-space: nowrap;
    overflow: hidden;
  }
  font-size: 11px;

  &:disabled {
    color: $ai-tutor-muted;
  }
}

.ai-tutor-composer__send {
  display: grid;
  place-items: center;
  flex: none;
  width: 39px;
  height: 39px;
  padding: 0;
  border: 0;
  border-radius: 12px;
  background: $ai-tutor-indigo;
  color: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }
}

// The prototype's plane points toward the start side in RTL; mirror it for
// LTR so it always points the way the text ends.
html:not(.is-rtl) .ai-tutor-composer__send ::v-deep svg {
  transform: scaleX(-1);
}

// P43-02 (Owner UAT): the image menu's rows go forward: «←» in Persian (as
// drawn), «→» in English.
html:not(.is-rtl) .ai-tutor-image-menu__action > ::v-deep svg {
  transform: scaleX(-1);
}

// WP-04V-D: desktop puts the composer inside the content area -- the column
// gutter on the far side, the sidebar plus the gutter on the near side -- so
// the auto margins land it on the chat column's own axis (measured x=137,
// w=920 at 1440). The single `borna-desktop` boundary replaces the old 767px
// media query (root cause R7).
@include borna-desktop {
  html.is-rtl .ai-tutor-composer {
    left: $borna-chat-gutter;
    right: $borna-sidebar-width + $borna-chat-gutter;
  }

  html:not(.is-rtl) .ai-tutor-composer {
    left: $borna-sidebar-width + $borna-chat-gutter;
    right: $borna-chat-gutter;
  }
}

// Mobile: the prototype floats the composer 4px clear of the bottom nav, which
// itself sits `$borna-bottom-nav-clearance` off the floor -- measured 77px in
// total. Derived from the nav's own token so the two can no longer drift.
@include borna-mobile {
  .ai-tutor-composer {
    bottom: calc(
      #{$borna-bottom-nav-clearance + 4px} + env(safe-area-inset-bottom)
    );
  }

  // P40 QA-47: while the Student types, the bottom navigation steps aside
  // (StudentBottomNav.vue) and the composer sits at the screen's bottom edge
  // -- or, with an on-screen keyboard over the layout viewport, right on top
  // of the keyboard instead of behind it.
  .ai-tutor-composer--typing {
    bottom: calc(8px + env(safe-area-inset-bottom));
  }

  .ai-tutor-composer--keyboard {
    bottom: calc(var(--ai-tutor-keyboard-inset) + 8px);
  }
}

// ---------------------------------------------------------------------------
// STUDENT CHAT V2 -- IMAGE ATTACHMENT FLOW. Exact values from the canonical V2
// prototype (sha256 6ddb695a...): `.chat-composer>button` (enabled / active /
// disabled), `.composer-media-preview`, `.composer-status-note(--image|
// --error)`, `.image-source-menu`, `.image-source-action`,
// `.visually-hidden-file`, with V2's <=620px and <=380px tiers.
// ---------------------------------------------------------------------------
$v2-midnight: $borna-ink;
$v2-indigo-700: $borna-indigo-900;
$v2-error: #d14545;
$v2-composer-height: 60px;

.ai-tutor-composer__tool {
  cursor: pointer;

  &--active {
    background: $borna-indigo-50;
    color: $v2-indigo-700;
  }

  &:disabled {
    opacity: 0.28;
    cursor: not-allowed;
  }
}

.ai-tutor-composer__file {
  position: fixed;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

// Stacked above the composer, like V2's `.chat-composer-dock` grid rows.
.ai-tutor-composer__above {
  position: absolute;
  right: 0;
  bottom: calc(100% + 7px);
  left: 0;
  display: grid;
  gap: 7px;
  justify-items: start;
}

.ai-tutor-composer__preview {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) 34px;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: min(100%, 520px);
  min-height: 68px;
  padding: 8px 10px;
  border: 1px solid rgba(153, 170, 232, 0.5);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 32px rgba(7, 20, 71, 0.12);

  img {
    display: block;
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: $ai-tutor-canvas;
    object-fit: cover;
  }

  > span {
    display: grid;
    gap: 2px;
    min-width: 0;
  }

  strong {
    color: $v2-midnight;
    font-size: 13px;
  }

  small {
    overflow: hidden;
    color: $ai-tutor-muted;
    font-size: 12px;
    line-height: 1.55;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: $ai-tutor-canvas;
    color: $ai-tutor-muted;
    cursor: pointer;
  }
}

.ai-tutor-composer__note {
  display: flex;
  align-items: center;
  gap: 7px;
  box-sizing: border-box;
  max-width: min(100%, 520px);
  padding: 8px 11px;
  border: 1px solid $borna-teal-100;
  border-radius: 11px;
  background: rgba(239, 252, 253, 0.98);
  box-shadow: 0 8px 22px rgba(7, 20, 71, 0.08);
  color: $borna-teal-700;
  font-size: 12px;
  font-weight: 750;
  line-height: 1.6;

  ::v-deep svg {
    flex: none;
  }

  &--image {
    border-color: $borna-indigo-100;
    background: rgba(246, 248, 255, 0.98);
    color: $v2-indigo-700;
  }

  &--error {
    border-color: #ffd6d6;
    background: #fff5f5;
    color: $v2-error;
  }
}

// Outside-click catcher; dimmed only on phones (V2).
.ai-tutor-image-menu__backdrop {
  position: fixed;
  inset: 0;
  z-index: 44;
  padding: 0;
  border: 0;
  background: transparent;
}

// A fixed box with exactly the composer's own horizontal geometry, sitting
// 10px above it; the menu hangs from its inline-start edge (V2 `right: 0` in
// RTL).
.ai-tutor-image-menu__anchor {
  position: fixed;
  left: 11px;
  right: 11px;
  bottom: calc(
    #{$borna-bottom-nav-clearance + 4px + $v2-composer-height + 10px} + env(safe-area-inset-bottom)
  );
  z-index: 45;
  display: flex;
  justify-content: flex-start;
  max-width: $borna-chat-column;
  margin-inline: auto;
  pointer-events: none;
}

.ai-tutor-image-menu {
  box-sizing: border-box;
  width: min(390px, 100%);
  padding: 15px;
  border: 1px solid rgba(153, 170, 232, 0.42);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  box-shadow: 0 24px 58px rgba(7, 20, 71, 0.2);
  pointer-events: auto;

  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;

    > div {
      display: grid;
      gap: 3px;
    }

    strong {
      color: $v2-midnight;
      font-size: 14px;
      line-height: 1.7;
    }

    span {
      color: $ai-tutor-muted;
      font-size: 12px;
      line-height: 1.7;
    }

    > button {
      display: grid;
      flex: 0 0 34px;
      place-items: center;
      width: 34px;
      height: 34px;
      padding: 0;
      border: 0;
      border-radius: 10px;
      background: $ai-tutor-canvas;
      color: $ai-tutor-muted;
      cursor: pointer;
    }
  }

  > div {
    display: grid;
    gap: 8px;
  }
}

.ai-tutor-image-menu__action {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 20px;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  width: 100%;
  min-height: 68px;
  padding: 10px 12px;
  border: 1px solid $ai-tutor-border;
  border-radius: 15px;
  background: #fff;
  color: $ai-tutor-text;
  font-family: inherit;
  text-align: start;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: $ai-tutor-indigo-300;
    background: $borna-indigo-50;
  }

  > span:nth-child(2) {
    display: grid;
    gap: 2px;
    min-width: 0;
  }

  strong {
    font-size: 14px;
  }

  small {
    color: $ai-tutor-muted;
    font-size: 12px;
    line-height: 1.55;
  }

  > ::v-deep svg {
    color: $ai-tutor-muted;
  }
}

.ai-tutor-image-menu__paste-hint {
  margin: 10px 2px 0;
  color: $ai-tutor-muted;
  font-size: 11px;
  line-height: 1.7;
}

.ai-tutor-image-menu__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: $borna-indigo-50;
  color: $v2-indigo-700;

  &--camera {
    background: $borna-teal-50;
    color: $borna-teal-700;
  }
}

// Desktop: the composer sits 20px off the floor inside the content area.
@include borna-desktop {
  html.is-rtl .ai-tutor-image-menu__anchor {
    left: $borna-chat-gutter;
    right: $borna-sidebar-width + $borna-chat-gutter;
  }

  html:not(.is-rtl) .ai-tutor-image-menu__anchor {
    left: $borna-sidebar-width + $borna-chat-gutter;
    right: $borna-chat-gutter;
  }

  .ai-tutor-image-menu__anchor {
    bottom: 20px + $v2-composer-height + 10px;
  }
}

// V2 <=620px: a bottom sheet over the composer, clear of the bottom nav, with
// two side-by-side source tiles and a dimmed backdrop.
@include borna-phone {
  .ai-tutor-composer__above {
    justify-items: stretch;
  }

  .ai-tutor-composer__preview {
    grid-template-columns: 46px minmax(0, 1fr) 32px;
    width: 100%;
    min-height: 62px;
    padding: 7px 9px;
    border-radius: 15px;

    img {
      width: 46px;
      height: 46px;
    }

    strong {
      font-size: 12px;
    }

    small {
      font-size: 11px;
    }

    button {
      width: 32px;
      height: 32px;
    }
  }

  .ai-tutor-composer__note {
    max-width: none;
    padding: 7px 9px;
    font-size: 11px;
  }

  .ai-tutor-image-menu__backdrop {
    background: rgba(7, 20, 71, 0.34);
    backdrop-filter: blur(3px);
  }

  // V2's own mobile override ("keep it clearly above the chat composer and
  // bottom navigation"): 148px off the floor, capped to the viewport.
  .ai-tutor-image-menu__anchor {
    left: 8px;
    right: 8px;
    bottom: calc(148px + env(safe-area-inset-bottom));
    max-width: none;
  }

  .ai-tutor-image-menu {
    width: 100%;
    max-height: calc(100vh - 180px);
    overflow: auto;
    overscroll-behavior: contain;
    border-radius: 22px;
    box-shadow: 0 24px 64px rgba(7, 20, 71, 0.3);

    > div {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .ai-tutor-image-menu__action {
    grid-template-columns: 1fr;
    align-content: center;
    justify-items: center;
    gap: 7px;
    min-height: 116px;
    padding: 12px 8px;
    text-align: center;

    > ::v-deep svg {
      display: none;
    }
  }
}

@media screen and (max-width: 360px) {
  .ai-tutor-image-menu__anchor {
    left: 7px;
    right: 7px;
    bottom: calc(144px + env(safe-area-inset-bottom));
  }
}

@include borna-narrow {
  .ai-tutor-composer__preview small {
    max-width: 210px;
  }

  .ai-tutor-image-menu__action {
    min-height: 94px;

    small {
      display: none;
    }
  }
}

// ---------------------------------------------------------------------------
// V405-B (doc 26 §6) -- the DOCKED composer of the chat start state. Values
// measured from the rendered V405 reference (`BORNA_final405-07-06.html`,
// sha256 ea21cc7b...9cfd) at 1440 and 390: `.chat-composer-dock`,
// `.chat-composer`, its tool buttons, input and send button, with the
// reference's 620px tier. Every rule is scoped under `--docked`, so the fixed
// composer of the active chat above is untouched.
//
// No `backdrop-filter` on the dock: it would become the containing block of
// the image menu's fixed backdrop (see the template note).
// ---------------------------------------------------------------------------
$docked-tool-ink: #5367c9;

.ai-tutor-composer-dock--docked {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  box-sizing: border-box;
  padding: 8px;
  border: 1px solid rgba(167, 184, 235, 0.82);
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    rgba(249, 251, 255, 0.97),
    rgba(239, 244, 255, 0.97)
  );
  box-shadow: 0 18px 50px rgba(34, 57, 128, 0.14),
    0 0 0 5px rgba(49, 75, 214, 0.035);
}

html .ai-tutor-composer-dock--docked .ai-tutor-composer,
html.is-rtl .ai-tutor-composer-dock--docked .ai-tutor-composer,
html:not(.is-rtl) .ai-tutor-composer-dock--docked .ai-tutor-composer {
  position: relative;
  top: auto;
  right: auto;
  bottom: auto;
  left: auto;
  z-index: auto;
  flex-wrap: wrap;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  min-height: 64px;
  margin: 0;
  padding: 8px;
  gap: 6px;
  border: 1.5px solid #b5c4ef;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: none;
  box-shadow: 0 10px 28px rgba(32, 57, 133, 0.08);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:focus-within {
    border-color: #6f83ec;
    box-shadow: 0 12px 30px rgba(49, 75, 214, 0.13),
      0 0 0 4px rgba(49, 75, 214, 0.075);
  }
}

.ai-tutor-composer-dock--docked {
  .ai-tutor-composer__tool {
    width: 40px;
    height: 40px;
    border: 1px solid #e0e6f5;
    border-radius: 14px;
    background: #f7f9ff;
    color: $docked-tool-ink;

    &:not(:disabled):not([aria-disabled='true']):hover,
    &--active {
      border-color: #c5d0f4;
      background: $borna-indigo-50;
      color: $borna-indigo-600;
    }
  }

  .ai-tutor-composer__input {
    height: 44px;
    // P39 TR-010: the 1.8 line where the input drew it (see above).
    padding: 8.5px 8px 6.7px;
    color: #172956;
    font-size: 16px;
    font-weight: $borna-fw-medium;
    line-height: 1.8;
    // In flow, so focusing it scrolls the page: keep it clear of the floor
    // (and, in the mobile shell, of the fixed bottom nav).
    scroll-margin-bottom: 24px;

    @include borna-mobile {
      scroll-margin-bottom: calc(
        #{$borna-bottom-nav-clearance + 24px} + env(safe-area-inset-bottom)
      );
    }

    &::placeholder {
      color: #96a1ba;
      opacity: 1;
    }

    // P39 TR-010: an input clipped an overlong placeholder at its content
    // edge; a textarea clips at its padding edge. Clip at the side padding
    // while the placeholder shows, so it ends where it did.
    &:placeholder-shown {
      clip-path: inset(0 8px);
    }
  }

  .ai-tutor-composer__send {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    background: linear-gradient(135deg, #526df2 0%, #314bd6 72%, #2440bd 100%);
    box-shadow: 0 10px 22px rgba(49, 75, 214, 0.28);

    &:disabled {
      background: #aab8eb;
      box-shadow: none;
      opacity: 0.72;
    }
  }

  // The preview and its notes take their own row inside the composer instead
  // of floating over the start guide.
  .ai-tutor-composer__above {
    position: static;
    order: -1;
    flex: 0 0 100%;
    margin-bottom: 2px;
  }
}

// Above the phone tier the image menu hangs from the docked composer itself;
// on phones it stays the existing bottom sheet.
@media screen and (min-width: $borna-bp-phone-max + 0.02px) {
  html .ai-tutor-composer-dock--docked .ai-tutor-image-menu__anchor,
  html.is-rtl .ai-tutor-composer-dock--docked .ai-tutor-image-menu__anchor,
  html:not(.is-rtl)
    .ai-tutor-composer-dock--docked
    .ai-tutor-image-menu__anchor {
    position: absolute;
    right: 0;
    bottom: calc(100% + 10px);
    left: 0;
    max-width: none;
    margin: 0;
  }
}

@include borna-phone {
  .ai-tutor-composer-dock--docked {
    padding: 6px;
    border-radius: 21px;
    box-shadow: 0 14px 38px rgba(34, 57, 128, 0.14),
      0 0 0 4px rgba(49, 75, 214, 0.03);

    .ai-tutor-composer__tool,
    .ai-tutor-composer__send {
      width: 36px;
      height: 40px;
    }

    .ai-tutor-composer__tool {
      border-radius: 13px;
    }

    .ai-tutor-composer__send {
      border-radius: 14px;
    }

    .ai-tutor-composer__input {
      height: 40px;
      // P39 TR-010: the line and the placeholder clip as above.
      padding: 6.5px 5px 4.7px;

      &:placeholder-shown {
        clip-path: inset(0 5px);
      }
    }
  }

  html .ai-tutor-composer-dock--docked .ai-tutor-composer,
  html.is-rtl .ai-tutor-composer-dock--docked .ai-tutor-composer,
  html:not(.is-rtl) .ai-tutor-composer-dock--docked .ai-tutor-composer {
    min-height: 58px;
    padding: 6px;
    gap: 4px;
    border-radius: 16px;
  }
}

// ---------------------------------------------------------------------------
// WB-M1A (doc 23 §13.4.3) -- the PANEL presentation, for the board Workspace's
// chat panel. The ordinary active-chat composer, in normal flow at the foot
// of its panel instead of fixed to the viewport floor. Every rule is scoped
// under `--panel`, so the fixed composer of the chat route and the `--docked`
// start composer above are untouched. Functional layout only: no reference is
// visually frozen for the Workspace.
// ---------------------------------------------------------------------------
.ai-tutor-composer-dock--panel {
  position: relative;
}

html .ai-tutor-composer-dock--panel .ai-tutor-composer,
html.is-rtl .ai-tutor-composer-dock--panel .ai-tutor-composer,
html:not(.is-rtl) .ai-tutor-composer-dock--panel .ai-tutor-composer {
  position: relative;
  top: auto;
  right: auto;
  bottom: auto;
  left: auto;
  z-index: auto;
  flex-wrap: wrap;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  margin: 0;
  backdrop-filter: none;
  box-shadow: 0 8px 22px rgba(7, 20, 71, 0.08);
}

// The notice (the zero-credit warning), the photo preview and its notes take
// their own row inside the composer instead of floating over the turns.
.ai-tutor-composer-dock--panel .ai-tutor-composer__above {
  position: static;
  order: -1;
  flex: 0 0 100%;
  margin-bottom: 2px;
}

// Above the phone tier the image menu hangs from the panel composer itself;
// on phones it stays the existing bottom sheet.
@media screen and (min-width: $borna-bp-phone-max + 0.02px) {
  html .ai-tutor-composer-dock--panel .ai-tutor-image-menu__anchor,
  html.is-rtl .ai-tutor-composer-dock--panel .ai-tutor-image-menu__anchor,
  html:not(.is-rtl)
    .ai-tutor-composer-dock--panel
    .ai-tutor-image-menu__anchor {
    position: absolute;
    right: 0;
    bottom: calc(100% + 10px);
    left: 0;
    max-width: none;
    margin: 0;
  }
}
// ---------------------------------------------------------------------------
// 37_VOICE_TO_TEXT (doc 37/01 §5) -- V405's voice states, values from the
// rendered canonical prototype (`BORNA_final405-07-06.html`, sha256
// ea21cc7b...9cfd): `.voice-recording-bar`, `.voice-transcribing`,
// `.voice-cancel` / `.voice-stop`, `.recording-dot`, `.voice-wave`,
// `.voice-spinner`, `.voice-ready-icon` and `.composer-media-preview--voice`,
// with V405's <=620px and <=380px tiers and its reduced-motion rule. Nothing
// here applies while the composer has no voice state.
// ---------------------------------------------------------------------------
$v405-teal-500: #14b7d7;
$v405-recording: #e74d5d;

// V405 gives the bar `width: 100%` inside a composer that never wraps; the
// docked and panel composers here DO wrap (their note row), so the bar takes
// the rest of the row instead -- the same box, beside a Board trigger too.
.ai-tutor-composer__voice-bar,
.ai-tutor-composer__voice-transcribing {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 12px;
  min-width: 0;

  > span:last-child {
    display: grid;
    gap: 1px;
  }

  strong {
    color: $v2-midnight;
    font-size: 13px;
  }

  small {
    color: $ai-tutor-muted;
    font-size: 12px;
  }
}

.ai-tutor-composer__voice-transcribing {
  justify-content: center;
}

.ai-tutor-composer__voice-cancel,
.ai-tutor-composer__voice-stop {
  display: grid;
  flex: 0 0 42px;
  place-items: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 0;
  border-radius: 13px;
  cursor: pointer;
}

.ai-tutor-composer__voice-cancel {
  background: $ai-tutor-canvas;
  color: $ai-tutor-muted;
}

.ai-tutor-composer__voice-stop {
  background: $borna-indigo-600;
  color: #fff;
  box-shadow: 0 7px 17px rgba(49, 75, 214, 0.22);
}

.ai-tutor-composer__voice-copy {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;

  > span:last-child {
    display: grid;
    gap: 1px;
  }

  strong {
    color: $v2-midnight;
    font-size: 13px;
  }

  small {
    color: $ai-tutor-muted;
    font-size: 12px;
  }
}

.ai-tutor-composer__voice-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: $v405-recording;
  box-shadow: 0 0 0 5px rgba(231, 77, 93, 0.12);
  animation: ai-tutor-recording-pulse 1.35s ease-in-out infinite;
}

.ai-tutor-composer__voice-wave {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  gap: 3px;
  min-width: 60px;
  height: 32px;
  overflow: hidden;

  i {
    width: 3px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(180deg, $v405-teal-500, $borna-indigo-600);
    animation: ai-tutor-voice-wave 0.9s ease-in-out infinite alternate;

    &:nth-child(3n) {
      animation-delay: -300ms;
    }

    &:nth-child(4n) {
      animation-delay: -550ms;
    }

    &:nth-child(5n) {
      animation-delay: -720ms;
    }
  }
}

.ai-tutor-composer__voice-spinner {
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  border: 3px solid $borna-indigo-100;
  border-top-color: $borna-indigo-600;
  border-radius: 50%;
  animation: ai-tutor-voice-spin 0.75s linear infinite;
}

// The voice card is the photo card's own box (V405 `.composer-media-preview
// --voice` repeats the same 52 / 34px columns, and its 620px tier the 46 /
// 32px ones), so no column rule of its own.
.ai-tutor-composer__preview > .ai-tutor-composer__voice-ready-icon {
  display: grid;
  gap: normal;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: $borna-teal-50;
  color: $borna-teal-700;
}

@keyframes ai-tutor-recording-pulse {
  50% {
    box-shadow: 0 0 0 8px rgba(231, 77, 93, 0.06);
  }
}

@keyframes ai-tutor-voice-wave {
  to {
    height: 25px;
  }
}

@keyframes ai-tutor-voice-spin {
  to {
    transform: rotate(360deg);
  }
}

@include borna-phone {
  .ai-tutor-composer__voice-cancel,
  .ai-tutor-composer__voice-stop {
    flex-basis: 40px;
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }

  .ai-tutor-composer__voice-bar {
    gap: 8px;
  }

  .ai-tutor-composer__voice-copy,
  .ai-tutor-composer__voice-transcribing {
    strong {
      font-size: 12px;
    }

    small {
      font-size: 11px;
    }
  }

  .ai-tutor-composer__voice-wave {
    gap: 2px;

    i {
      width: 2px;
    }
  }

  .ai-tutor-composer__preview > .ai-tutor-composer__voice-ready-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
  }
}

@include borna-narrow {
  .ai-tutor-composer__voice-copy > span:last-child,
  .ai-tutor-composer__voice-wave i:nth-child(n + 17) {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-tutor-composer__voice-dot,
  .ai-tutor-composer__voice-wave i,
  .ai-tutor-composer__voice-spinner {
    animation: none;
  }
}
</style>
